import { differenceInSeconds } from "date-fns";

import { Duration } from "@shared/interfaces/duration.interface";

/** How the time of a finished "Descarregando" state splits between unloading and idling at the jobsite. */
export interface DischargeBreakdown {
  /** Whole state, from its start until the vehicle left the jobsite */
  totalSeconds: number;
  /** Part of the state with the drum still unloading */
  unloadingSeconds: number;
  /** Part of the state after the drum stopped unloading (post-unload idleness) */
  idleSeconds: number;
}

/**
 * Converts a duration into seconds.
 * @param duration Duration with optional hours and minutes
 * @returns Total seconds
 */
export const durationToSeconds = (duration: Duration): number => (
  (duration.hours || 0) * 3600 + (duration.minutes || 0) * 60 + (duration.seconds || 0)
);

/**
 * Splits a finished "Descarregando" state into unloading and post-unload idleness.
 * The idleness is already part of the state, so the two parts always add up to the state duration.
 * @param start When the state started
 * @param finish When the state ended (the vehicle left the jobsite)
 * @param idle Post-unload idleness of the travel
 * @returns The split, or undefined while the state is open or without idleness
 */
export const getDischargeBreakdown = (
  start: Date | string | undefined,
  finish: Date | string | undefined,
  idle: Duration | undefined
): DischargeBreakdown | undefined => {

  if (!start || !finish || !idle) return undefined;

  const totalSeconds = Math.max(differenceInSeconds(new Date(finish), new Date(start)), 0);
  const idleSeconds = Math.min(Math.max(durationToSeconds(idle), 0), totalSeconds);

  return { totalSeconds, unloadingSeconds: totalSeconds - idleSeconds, idleSeconds };
};
